// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
  IconButton,
  Box,
  useTheme,
} from "@mui/material";
import Loading from "./LinearLoading";
import { ErrorSubComponent } from "../pages/ErrorPage";
import NoDataAvailable from "./NoDataAvailable";
import { DataStruct, caseDetailsWithCount } from "../data/utils/types";
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";

// Define proper error type for API responses
interface ApiError {
  message: string;
  status: number;
}



interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

export interface TableDataProps {
  data: DataStruct[] | caseDetailsWithCount | undefined;
  loading: boolean;
  error: ApiError | Error | null;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  colNameArray: string[];
  colAttributeArray: string[];
  handleRowClick?: (rowData: any) => void;
}

export default function DefaultTable(parentProps: TableDataProps) {

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    parentProps.setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    parentProps.setRowsPerPage(parseInt(event.target.value, 10));
    parentProps.setPage(0);
  };

  // Type guard to check if data is caseDetailsWithCount
  const isCaseDetailsWithCount = (data: any): data is caseDetailsWithCount => {
    return (
      data &&
      typeof data === 'object' &&
      typeof data.count === 'number' &&
      Array.isArray(data.cases)
    );
  };

  // Enhanced pagination logic to handle both counted and uncounted data
  const getPaginationProps = () => {
    const { data } = parentProps;

    // Check if data is caseDetailsWithCount using proper type guard
    if (isCaseDetailsWithCount(data)) {
      return {
        count: data.count,
        actualData: data.cases,
        hasTotal: true
      };
    }

    // For regular arrays (accounts, projects, etc.)
    if (Array.isArray(data)) {
      return {
        count: -1, // Unknown total, use smart pagination
        actualData: data,
        hasTotal: false
      };
    }

    return {
      count: 0,
      actualData: [],
      hasTotal: false
    };
  };

  const { count, actualData, hasTotal } = getPaginationProps();

  return (
    <>
      {parentProps.loading ? (
        <Loading />
      ) : parentProps.error ? (
        <ErrorSubComponent />
      ) : (parentProps.data &&
        <PopulateTable
          data={actualData}
          rowsPerPage={parentProps.rowsPerPage}
          page={parentProps.page}
          count={count}
          hasTotal={hasTotal}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          colNameArray={parentProps.colNameArray}
          colAttributeArray={parentProps.colAttributeArray}
          handleRowClick={parentProps.handleRowClick}
        />
      )}
    </>
  );
}

const PopulateTable = ({
  data,
  rowsPerPage,
  page,
  count,
  hasTotal,
  handleChangePage,
  handleChangeRowsPerPage,
  colNameArray,
  colAttributeArray,
  handleRowClick,
}: {
  data: DataStruct[];
  rowsPerPage: number;
  page: number;
  count: number;
  hasTotal: boolean;
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  colNameArray: string[];
  colAttributeArray: string[];
  handleRowClick?: (rowData: any) => void;
}) => {

  const handleRowClickInternal = (rowData: any) => {
    if (handleRowClick) {
      handleRowClick(rowData);
    }
  };

  // Show custom no data component instead of empty table
  if (data && data.length === 0) {
    return (
      <NoDataAvailable
        message="No data available"
        description="There are no items to display. Try adjusting your filters or check back later."
      />
    );
  }

  return (
    <Paper sx={{ 
      width: "100%", 
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      overflow: "hidden",
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <TableContainer sx={{ 
        maxHeight: '70vh',
        overflow: 'auto',
        margin: 0,
        padding: 0,
      }}>
        <Table stickyHeader aria-label="sticky table" sx={{ margin: 0 }}>
          <TableHead>
            <TableRow>
              {colNameArray.map((value: string, index: number) => (
                <TableCell key={index} className="fw-bold t-col-size"
                  sx={{ 
                    backgroundColor: '#e0e0e0',
                    color: '#000000',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: 'bold',
                    margin: 0,
                    padding: '12px 16px',
                  }}>
                  {value}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data && data.map((dataRow: DataStruct, index: number) => (
              <TableRow key={index} onClick={() => handleRowClickInternal(dataRow)}
                sx={{
                  ...(handleRowClick ? {
                    cursor: "pointer", 
                  } : {}),
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafafa',
                  },
                  '&:hover': {
                      backgroundColor: '#f9dcc5',
                    },
                  borderBottom: '1px solid #e0e0e0',
                }}>
                {colAttributeArray.map((attributeName: string, index: number) => (
                  <TableCell key={index} className="t-col-size">
                    {dataRow[attributeName]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{
        borderTop: '3px solid rgb(133, 142, 149)',
        backgroundColor: '#f8f9fa',
        padding: '8px 16px',
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
      }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: {
              "aria-label": "rows per page",
            },
            native: false,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={(props) => (
            <TablePaginationActions 
              {...props} 
              hasTotal={hasTotal}
              currentDataLength={data.length}
            />
          )}
          sx={{
            '& .MuiTablePagination-toolbar': {
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingLeft: 0,
            },
            '& .MuiTablePagination-spacer': {
              display: 'none', // Remove spacer that pushes content to right
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#000000',
              margin: '0 8px 0 0',
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.875rem',
              margin: '0 6px 0 2px',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              minWidth: '50px',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
              fontWeight: 500,
              color:'#000000',
              margin: '0 12px 0 0',
            },
            '& .MuiTablePagination-actions': {
              marginLeft: '4px',
            }
          }}
        />
      </Box>
    </Paper>
  );
};

function TablePaginationActions(props: TablePaginationActionsProps & {
  hasTotal: boolean;
  currentDataLength: number;
}) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange, hasTotal, currentDataLength } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  // Smart pagination logic for next button
  const isNextDisabled = () => {
    if (hasTotal && count !== -1) {
      // Use actual total count when available
      return page >= Math.ceil(count / rowsPerPage) - 1;
    } else {
      // For APIs without total count, disable next if current page has fewer items than requested
      return currentDataLength < rowsPerPage;
    }
  };

  // Smart pagination logic for last button
  const isLastDisabled = () => {
    if (hasTotal && count !== -1) {
      return page >= Math.ceil(count / rowsPerPage) - 1;
    } else {
      // Disable last button for unknown total count
      return true;
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={isNextDisabled()}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={isLastDisabled()}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}
